/**
 * Community data client for the Terrace, the Gauge, and match predictions.
 * Online it talks to the platform routes (/api/coyg/*); offline it degrades
 * to the last-good localStorage snapshot, then to the seeded launch takes.
 * Writes are optimistic; the server is the tie-breaker on the next fetch.
 */
import { SEED_TAKES } from '../data/takes-seed';
import { getAnonKey, getWritableAnonKey, isValidCustomHandle, normalizeHandle, storeHandle } from './handle';
import { markParticipated } from './participation';
import { mutateGamePassport, readGamePassport, recordConfirmedPrediction } from './game-passport';
import { announceWritePersistenceFailure, captureLocalCommunitySnapshot, isLocalCommunityGenerationCurrent, eraseLocalCommunityData, queueWrite, removeQueuedWrite, transmitIfLocalCommunityGeneration } from './outbox';
import { STANDALONE_DATA_EPOCH } from './release';
import { retryAfterMs } from './public-reads';
import type {
  GaugeSummary,
  Mood,
  PredictionPick,
  PredictionSummary,
  ReportReason,
  Take,
  TakesActivity,
  TakeStub,
  Thread,
  VoteDir,
} from './types';

// Same-origin in production; the platform dev server in development.
export const API_BASE = import.meta.env.VITE_API_ORIGIN ?? '';

const LEGACY_TAKES_CACHE = 'coyg_takes_cache';
const GAUGE_CACHE = 'coyg_gauge_cache';
const LOCAL_TAKES = 'coyg_local_takes';
const PREDICT_CACHE = 'coyg_predict_cache';
const COMMUNITY_CONSENT_KEY = 'coyg_community_consent';
const latestGaugeWrite = new Map<string, string>();
const latestPredictionWrite = new Map<string, string>();
const predictionWriteEpoch = new Map<string, number>();
const predictionReadEpoch = new Map<string, number>();
const latestClientRevision = new Map<string, number>();

let revisionLock: Promise<void> = Promise.resolve();
async function nextClientRevision(kind: 'gauge' | 'prediction', matchId: string): Promise<number> {
  const key = `${kind}:${matchId}`;
  const create = () => {
    const storageKey = `coyg_revision:${key}`;
    let stored = 0;
    try { stored = Number(localStorage.getItem(storageKey) ?? 0); } catch { /* durable identity gate already passed */ }
    const revision = Math.max(Date.now(), stored + 1, (latestClientRevision.get(key) ?? 0) + 1);
    latestClientRevision.set(key, revision);
    try { localStorage.setItem(storageKey, String(revision)); } catch { /* operation id still prevents equal-write mutation */ }
    return revision;
  };
  if (typeof navigator !== 'undefined' && navigator.locks) return navigator.locks.request(`coyg-revision:${key}`, { mode: 'exclusive' }, create);
  const previous = revisionLock;
  let release!: () => void;
  revisionLock = new Promise<void>((resolve) => { release = resolve; });
  await previous;
  try { return create(); } finally { release(); }
}
export const COMMUNITY_CONSENT_EVENT = 'coyg:community-consent';
export const COMMUNITY_POLICY_VERSION = '2026-08-23';

export function hasCommunityConsent(): boolean {
  try {
    return localStorage.getItem(COMMUNITY_CONSENT_KEY) === COMMUNITY_POLICY_VERSION;
  } catch {
    return false;
  }
}

export function acceptCommunityConsent(): boolean {
  try {
    localStorage.setItem(COMMUNITY_CONSENT_KEY, COMMUNITY_POLICY_VERSION);
    if (localStorage.getItem(COMMUNITY_CONSENT_KEY) !== COMMUNITY_POLICY_VERSION) throw new Error('consent-not-persisted');
    window.dispatchEvent(new Event(COMMUNITY_CONSENT_EVENT));
    return true;
  } catch {
    announceWritePersistenceFailure('take');
    return false;
  }
}

function privateRead(): RequestInit {
  return { cache: 'no-store', headers: { 'x-coyg-anon-key': getAnonKey() } };
}

function readJSON<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJSON(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full / private mode */
  }
}

// ── Takes ────────────────────────────────────────────────────────────────────

/** Legacy local takes are read only for safe cleanup; new creative writes are online-only. */
function localTakes(): Take[] {
  return readJSON<Take[]>(LOCAL_TAKES) ?? [];
}

export function fallbackTakes(): Take[] {
  const cached = readJSON<TakeScopeSnapshot>(takesCacheKey({ room: 'all', matchId: null }))?.takes
    ?? readJSON<Take[]>(LEGACY_TAKES_CACHE);
  const base = cached ?? SEED_TAKES;
  const seen = new Set(base.map((t) => t.id));
  return [...localTakes().filter((t) => !seen.has(t.id)), ...base];
}

export interface TakesResult {
  /** Backoff for automatic reads only; never exposes the device identity. */
  retryAfterMs?: number;
  takes: Take[];
  stubs: TakeStub[];
  dampener: { player: string } | null;
  activity: TakesActivity;
  online: boolean;
}

export type TakeRoom = 'all' | 'match' | 'TRANSFERS' | 'SEASON' | 'ANALYSIS';

export interface TakeReadScope {
  room?: TakeRoom;
  matchId?: string | null;
  signal?: AbortSignal;
}

type NormalizedTakeScope = { room: TakeRoom; matchId: string | null; signal?: AbortSignal };
type TakeScopeSnapshot = Omit<TakesResult, 'online'>;

const EMPTY_TAKES_ACTIVITY: TakesActivity = {
  checkedAt: new Date(0).toISOString(),
  latestVisibleAt: null,
  publicTakesLastHour: 0,
};

function normalizeTakeScope(scope?: string | null | TakeReadScope): NormalizedTakeScope {
  if (typeof scope === 'string') return { room: 'match', matchId: scope };
  return {
    room: scope?.room ?? 'all',
    matchId: scope?.room === 'match' ? scope.matchId ?? null : null,
    signal: scope?.signal,
  };
}

function takesCacheKey(scope: Pick<NormalizedTakeScope, 'room' | 'matchId'>): string {
  const suffix = scope.room === 'match' && scope.matchId
    ? `match:${encodeURIComponent(scope.matchId)}`
    : `room:${scope.room}`;
  return `coyg:${STANDALONE_DATA_EPOCH}:takes:${suffix}`;
}

function takeMatchesScope(take: Take, scope: Pick<NormalizedTakeScope, 'room' | 'matchId'>): boolean {
  if (scope.room === 'all') return true;
  if (scope.room === 'match') {
    return take.thread === 'MATCH' && (!scope.matchId || take.matchId === scope.matchId);
  }
  return take.thread === scope.room;
}

function fallbackActivity(takes: Take[]): TakesActivity {
  const visibleTimes = takes.map((take) => take.createdAt).filter(Number.isFinite);
  return {
    ...EMPTY_TAKES_ACTIVITY,
    latestVisibleAt: visibleTimes.length ? Math.max(...visibleTimes) : null,
  };
}

/**
 * Read one isolated North Bank scope. A string remains a temporary shorthand
 * for the historic `fetchTakes(matchId)` call and now correctly means MATCH.
 */
const takeBackoffs = new Map<string, { until: number; failures: number }>();

export async function fetchTakes(scopeInput?: string | null | TakeReadScope): Promise<TakesResult> {
  const scope = normalizeTakeScope(scopeInput);
  const identity = await captureLocalCommunitySnapshot(() => getAnonKey());
  const empty = (): TakesResult => ({ takes: [], stubs: [], dampener: null, activity: EMPTY_TAKES_ACTIVITY, online: false });
  if (!identity.value) return empty();
  const retryKey = `${identity.value}:${takesCacheKey(scope)}`;
  const previous = takeBackoffs.get(retryKey);
  let retryMs = Math.max(0, (previous?.until ?? 0) - Date.now());
  try {
    if (retryMs > 0) throw new Error('read-backoff');
    const query = new URLSearchParams();
    if (scope.room === 'match') query.set('thread', 'MATCH');
    else if (scope.room !== 'all') query.set('thread', scope.room);
    if (scope.room === 'match' && scope.matchId) query.set('match', scope.matchId);
    const suffix = query.size ? `?${query}` : '';
    const request: RequestInit = { cache: 'no-store', headers: { 'x-coyg-anon-key': identity.value }, signal: AbortSignal.timeout(15_000) };
    if (scope.signal) request.signal = scope.signal;
    const res = await fetch(`${API_BASE}/api/coyg/takes${suffix}`, request);
    if (res.ok) {
      const body = (await res.json()) as { takes: Array<Take | TakeStub>; dampener?: { player: string }; activity?: TakesActivity };
      if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return empty();
      if (Array.isArray(body.takes)) {
        const stubs = body.takes.filter((t): t is TakeStub => (t as TakeStub).status === 'hidden');
        const visible = body.takes.filter((t): t is Take => (t as TakeStub).status !== 'hidden');
        // Server now owns anything it returned; drop confirmed local copies.
        const serverTexts = new Set(visible.map((t) => `${t.handle}:${t.text}`));
        const pending = localTakes().filter((t) => !serverTexts.has(`${t.handle}:${t.text}`));
        writeJSON(LOCAL_TAKES, pending);
        const localForView = pending.filter((take) => takeMatchesScope(take, scope));
        const activity = body.activity ?? fallbackActivity(visible);
        const snapshot: TakeScopeSnapshot = { takes: visible, stubs, dampener: body.dampener ?? null, activity };
        writeJSON(takesCacheKey(scope), snapshot);
        takeBackoffs.delete(retryKey);
        const result = { ...snapshot, takes: [...localForView, ...visible], online: true };
        return result;
      }
    }
    const failures = (previous?.failures ?? 0) + 1;
    retryMs = Math.max(retryAfterMs(res.headers.get('retry-after')), Math.min(15 * 60_000, 60_000 * 2 ** Math.min(failures - 1, 4)));
    takeBackoffs.set(retryKey, { until: Date.now() + retryMs, failures });
  } catch {
    if (!scope.signal?.aborted && retryMs <= 0) {
      const failures = (previous?.failures ?? 0) + 1;
      retryMs = Math.min(15 * 60_000, 60_000 * 2 ** Math.min(failures - 1, 4));
      takeBackoffs.set(retryKey, { until: Date.now() + retryMs, failures });
    }
  }
  if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return empty();
  const snapshot = readJSON<TakeScopeSnapshot>(takesCacheKey(scope));
  if (snapshot) {
    const known = new Set(snapshot.takes.map((take) => take.id));
    const pending = localTakes().filter((take) => takeMatchesScope(take, scope) && !known.has(take.id));
    return { ...snapshot, takes: [...pending, ...snapshot.takes], online: false, retryAfterMs: retryMs };
  }
  const scopedFallback = fallbackTakes().filter((take) => takeMatchesScope(take, scope));
  return {
    takes: scopedFallback,
    stubs: [],
    dampener: null,
    activity: fallbackActivity(scopedFallback),
    online: false, retryAfterMs: retryMs,
  };
}

/** Delete your own take. Server authorises by anonKey (handles aren't proof). */
export async function deleteTake(takeId: string): Promise<boolean> {
  const anonKey = getWritableAnonKey();
  if (!anonKey) { announceWritePersistenceFailure('take'); return false; }
  try {
    const res = await fetch(`${API_BASE}/api/coyg/takes`, {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ takeId, anonKey, dataEpoch: STANDALONE_DATA_EPOCH }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** The full reply list for one take (the "+N added" drawer). */
export async function fetchReplies(parentId: string): Promise<Take[] | null> {
  const replies: Take[] = [];
  let cursor: { afterAt: number; afterId: string } | null = null;
  try {
    do {
      const query = new URLSearchParams({ parent: parentId });
      if (cursor) {
        query.set('afterAt', String(cursor.afterAt));
        query.set('afterId', cursor.afterId);
      }
      const res = await fetch(`${API_BASE}/api/coyg/takes?${query}`, privateRead());
      if (!res.ok) return null;
      const body = (await res.json()) as { replies: Take[]; nextCursor?: { afterAt: number; afterId: string } | null };
      if (!Array.isArray(body.replies)) return null;
      replies.push(...body.replies);
      cursor = body.nextCursor && Number.isSafeInteger(body.nextCursor.afterAt) && typeof body.nextCursor.afterId === 'string'
        ? body.nextCursor
        : null;
    } while (cursor);
    return replies;
  } catch {
    /* offline — the inlined replies stand */
  }
  return null;
}

/**
 * Persist a composed take. The caller shows `optimistic` instantly; this
 * trades it for the server's copy when online. Creative writes are never
 * replayed later, so a pause or sanction cannot be bypassed by an old queue.
 */
/** Why the server refused a take (it was reached, but said no) — distinct from
 *  offline. `closed` = the match is in the book; `cooldown`/`rate-limited` =
 *  posting too fast; `error` = anything else. None of these persist locally. */
export type TakeRejection = 'closed' | 'cooldown' | 'rate-limited' | 'consent-required' | 'handle-conflict' | 'suspended' | 'banned' | 'community-paused' | 'error';

export async function postTake(
  optimistic: Take,
): Promise<{ take: Take; online: boolean; blocked?: boolean; rejected?: TakeRejection; handleChanged?: string }> {
  if (!hasCommunityConsent()) {
    return { take: optimistic, online: true, rejected: 'consent-required' };
  }
  const identity = await captureLocalCommunitySnapshot(getWritableAnonKey);
  const anonKey = identity.value;
  if (!anonKey) {
    announceWritePersistenceFailure('take');
    return { take: optimistic, online: true, rejected: 'error' };
  }
  const payload = {
    handle: optimistic.handle,
    thread: optimistic.thread,
    text: optimistic.text,
    matchId: optimistic.matchId,
    parentId: optimistic.parentId ?? undefined,
    clientId: optimistic.id,
    anonKey,
    dataEpoch: STANDALONE_DATA_EPOCH,
    ageConfirmed: true,
    communityPolicy: COMMUNITY_POLICY_VERSION,
  };
  try {
    const delivery = await transmitIfLocalCommunityGeneration(identity.generation, () => fetch(`${API_BASE}/api/coyg/takes`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(10_000),
    }), 'shared');
    if (!delivery.transmitted) return { take: optimistic, online: false, rejected: 'error' };
    const res = delivery.value;
    if (res.ok) {
      const body = (await res.json()) as { take: Take; handleChanged?: string; clientId?: string };
      const committed = await mutateGamePassport((state) => state, identity.generation, () => {
        if (body.handleChanged) storeHandle(body.handleChanged);
        if (optimistic.matchId) markParticipated(optimistic.matchId);
      });
      if (!committed || !(await isLocalCommunityGenerationCurrent(identity.generation))) return { take: optimistic, online: false, rejected: 'error' };
      return { take: body.take, online: true, handleChanged: body.handleChanged };
    }
    if (res.status === 429 || res.status >= 500) return { take: optimistic, online: false, rejected: 'error' };
    // The server was REACHED but refused — never persist locally (that would
    // strand a phantom take the server rejected). Map the status to a reason.
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    if (res.status === 400 && body?.error === 'blocked-language') {
      return { take: optimistic, online: true, blocked: true };
    }
    const rejected: TakeRejection =
      res.status === 423 && body?.error === 'community-paused'
        ? 'community-paused'
        : res.status === 403 && body?.error === 'suspended'
          ? 'suspended'
          : res.status === 403 && body?.error === 'banned'
            ? 'banned'
            :
      res.status === 409
        ? body?.error === 'handle-conflict' ? 'handle-conflict' : 'closed'
        : res.status === 429
          ? 'cooldown'
          : res.status === 403 && body?.error === 'community-consent-required'
            ? 'consent-required'
            : 'error';
    return { take: optimistic, online: true, rejected };
  } catch {
    return { take: optimistic, online: false, rejected: 'error' };
  }
}

export function buildTake(
  handle: string,
  thread: Thread,
  text: string,
  matchId: string | null = null,
  parentId: string | null = null,
): Take {
  return {
    id: `local-${crypto.randomUUID()}`,
    handle,
    thread,
    text,
    matchId,
    parentId,
    up: 0,
    createdAt: Date.now(),
    myVote: null,
    mine: true,
  };
}

/**
 * Toggle appreciation on a take. One positive signal — `on` true appreciates,
 * false clears it. No downvote exists. Returns the new count, or null offline.
 */
const appreciationQueues = new Map<string, Promise<{ up: number; myVote: VoteDir } | null>>();

export async function appreciate(takeId: string, on: boolean): Promise<{ up: number; myVote: VoteDir } | null> {
  if (!hasCommunityConsent()) return null;
  const anonKey = getWritableAnonKey();
  if (!anonKey) { announceWritePersistenceFailure('take'); return null; }
  const send = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/coyg/votes`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ takeId, anonKey, dir: on ? 'up' : null, dataEpoch: STANDALONE_DATA_EPOCH, ageConfirmed: true, communityPolicy: COMMUNITY_POLICY_VERSION }),
      });
      if (res.ok) {
        const body = (await res.json()) as { up: number; myVote: VoteDir };
        return { up: body.up, myVote: body.myVote };
      }
    } catch {
      /* Appreciation is online-only; callers restore the last confirmed vote. */
    }
    return null;
  };
  const run = async (): Promise<{ up: number; myVote: VoteDir } | null> => {
    if (typeof navigator !== 'undefined' && navigator.locks) {
      return navigator.locks.request(`coyg:appreciation:${takeId}`, async () => send());
    }
    return send();
  };
  const prior = appreciationQueues.get(takeId) ?? Promise.resolve(null);
  const pending = prior.catch(() => null).then(run);
  appreciationQueues.set(takeId, pending);
  return pending.finally(() => {
    if (appreciationQueues.get(takeId) === pending) appreciationQueues.delete(takeId);
  });
}

export async function postReport(takeId: string, reason: ReportReason): Promise<{ ok: boolean; hidden: boolean; queued: boolean }> {
  const identity = await captureLocalCommunitySnapshot(getWritableAnonKey);
  const anonKey = identity.value;
  if (!anonKey) { announceWritePersistenceFailure('report'); return { ok: false, hidden: false, queued: false }; }
  const clientId = crypto.randomUUID();
  const reportPayload = { takeId, anonKey, reason, clientId, dataEpoch: STANDALONE_DATA_EPOCH };
  const persisted = await queueWrite({ id: clientId, kind: 'report', body: reportPayload, ts: Date.now() }, identity.generation);
  if (!persisted) {
    announceWritePersistenceFailure('report');
    return { ok: false, hidden: false, queued: false };
  }
  try {
    const delivery = await transmitIfLocalCommunityGeneration(identity.generation, () => fetch(`${API_BASE}/api/coyg/reports`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(reportPayload),
      signal: AbortSignal.timeout(10_000),
    }));
    if (!delivery.transmitted) return { ok: false, hidden: false, queued: false };
    const res = delivery.value;
    if (res.ok) {
      await removeQueuedWrite(clientId, identity.generation);
      return { ...(await res.json()) as { ok: boolean; hidden: boolean }, queued: false };
    }
    if (res.status === 429 || res.status >= 500) return { ok: false, hidden: false, queued: persisted };
    await removeQueuedWrite(clientId, identity.generation);
  } catch {
    /* Offline/timeout — keep the preflight-persisted report for replay. */
    return { ok: false, hidden: false, queued: persisted };
  }
  return { ok: false, hidden: false, queued: false };
}

// ── Community safety requests ───────────────────────────────────────────────

export type SafetyRequestKind = 'complaint' | 'appeal' | 'privacy';
export interface SafetyRequest {
  id: string;
  kind: SafetyRequestKind;
  take_id: string | null;
  sanction_id: string | null;
  body: string;
  status: 'open' | 'reviewing' | 'resolved' | 'rejected';
  created_at: string;
  updated_at: string;
  reviewer_note: string | null;
}

export async function submitSafetyRequest(
  kind: SafetyRequestKind,
  requestBody: string,
  sanctionId?: string | null,
): Promise<{ ok: boolean; reference?: string; queued?: boolean; error?: 'too-short' | 'rate-limited' | 'offline' | 'error' }> {
  const trimmed = requestBody.trim();
  if (trimmed.length < 20) return { ok: false, error: 'too-short' };
  const identity = await captureLocalCommunitySnapshot(getWritableAnonKey);
  const durableAnonKey = identity.value;
  // The privacy/contact channel must still work when browser storage is
  // denied. In that case use a one-request key: the supporter keeps the
  // returned reference, but this browser cannot later list the request.
  const anonKey = durableAnonKey ?? `contact-${crypto.randomUUID()}-${crypto.randomUUID()}`;
  const clientId = crypto.randomUUID();
  const requestPayload = { anonKey, kind, sanctionId: sanctionId ?? undefined, body: trimmed.slice(0, 2000), clientId, dataEpoch: STANDALONE_DATA_EPOCH };
  const persisted = durableAnonKey
    ? await queueWrite({ id: clientId, kind: 'safety', body: requestPayload, ts: Date.now() }, identity.generation)
    : false;
  try {
    const delivery = await transmitIfLocalCommunityGeneration(identity.generation, () => fetch(`${API_BASE}/api/coyg/safety-requests`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(requestPayload),
      signal: AbortSignal.timeout(10_000),
    }));
    if (!delivery.transmitted) return { ok: false, error: 'error' };
    const res = delivery.value;
    if (res.ok) {
      const result = (await res.json()) as { reference: string };
      if (persisted) await removeQueuedWrite(clientId, identity.generation);
      return { ok: true, reference: result.reference };
    }
    if (res.status === 429) return { ok: false, error: 'rate-limited' };
    if (res.status >= 500) return persisted ? { ok: true, reference: clientId, queued: true } : { ok: false, error: 'offline' };
    if (persisted) await removeQueuedWrite(clientId, identity.generation);
    return { ok: false, error: res.status === 429 ? 'rate-limited' : 'error' };
  } catch {
    return persisted ? { ok: true, reference: clientId, queued: true } : { ok: false, error: 'offline' };
  }
}

export interface ParticipationStatus {
  available: boolean;
  writesEnabled: boolean;
  community: { writesEnabled: boolean; reason: string | null; updatedAt: number };
  sanction: null | {
    id: string;
    kind: 'suspended' | 'banned';
    reason: string;
    createdAt: number;
    expiresAt: number | null;
    appealPath: string;
  };
}

export async function fetchParticipationStatus(): Promise<ParticipationStatus | null> {
  try {
    const res = await fetch(`${API_BASE}/api/coyg/participation-status`, privateRead());
    if (!res.ok) return null;
    return await res.json() as ParticipationStatus;
  } catch {
    return null;
  }
}

export async function fetchSafetyRequests(): Promise<SafetyRequest[]> {
  const read = async (url: string): Promise<SafetyRequest[]> => {
    try {
      const res = await fetch(url, { ...privateRead(), signal: AbortSignal.timeout(5_000) });
      if (!res.ok) return [];
      const result = (await res.json()) as { requests?: SafetyRequest[] };
      return Array.isArray(result.requests) ? result.requests : [];
    } catch { return []; }
  };
  return read(`${API_BASE}/api/coyg/safety-requests`);
}

// ── Handle (editable name) ─────────────────────────────────────────────────────

/** Is this name still free? Online check; offline we optimistically allow it. */
export async function checkHandle(name: string): Promise<{ available: boolean }> {
  if (!isValidCustomHandle(name)) return { available: false };
  try {
    const res = await fetch(
      `${API_BASE}/api/coyg/handle?handle=${encodeURIComponent(normalizeHandle(name))}`,
      privateRead(),
    );
    if (res.ok) {
      const body = (await res.json()) as { available?: boolean };
      return { available: body.available !== false };
    }
  } catch {
    /* offline — let the claim be the real gate */
  }
  return { available: true };
}

/**
 * Claim a new name. On success the handle is stored locally and returned.
 * `taken` means another fan already has it; `invalid` means it failed the
 * format/profanity gate. Offline we can't guarantee uniqueness, so we refuse.
 */
export async function claimHandle(
  name: string,
): Promise<{ ok: true; handle: string } | { ok: false; error: 'taken' | 'invalid' | 'offline' | 'consent' }> {
  if (!isValidCustomHandle(name)) return { ok: false, error: 'invalid' };
  if (!hasCommunityConsent()) return { ok: false, error: 'consent' };
  const anonKey = getWritableAnonKey();
  if (!anonKey) return { ok: false, error: 'offline' };
  try {
    const res = await fetch(`${API_BASE}/api/coyg/handle`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ anonKey, handle: normalizeHandle(name), dataEpoch: STANDALONE_DATA_EPOCH, ageConfirmed: true, communityPolicy: COMMUNITY_POLICY_VERSION }),
    });
    if (res.ok) {
      const body = (await res.json()) as { handle: string };
      storeHandle(body.handle);
      return { ok: true, handle: body.handle };
    }
    if (res.status === 409) return { ok: false, error: 'taken' };
    if (res.status === 403) return { ok: false, error: 'consent' };
    return { ok: false, error: 'invalid' };
  } catch {
    return { ok: false, error: 'offline' };
  }
}

// ── Gauge ────────────────────────────────────────────────────────────────────

export const GAUGE_EMPTY: GaugeSummary = {
  avg: null,
  count: 0,
  moods: { buzzing: 0, relieved: 0, anxious: 0, frustrated: 0 },
  aggregateAvailable: false,
  moodAggregateAvailable: false,
  mine: null,
};

export function privacySafeGauge(summary: GaugeSummary): GaugeSummary {
  return {
    ...GAUGE_EMPTY,
    mine: summary.mine ?? null,
  };
}

export function fallbackGauge(matchId?: string): GaugeSummary {
  const cached = readJSON<GaugeSummary>(matchId ? `${GAUGE_CACHE}:${matchId}` : GAUGE_CACHE);
  return cached ? privacySafeGauge(cached) : GAUGE_EMPTY;
}

export async function fetchGauge(matchId: string): Promise<{ gauge: GaugeSummary; online: boolean }> {
  const identity = await captureLocalCommunitySnapshot(getAnonKey);
  if (!identity.value) return { gauge: GAUGE_EMPTY, online: false };
  const storedRevision = () => { try { return localStorage.getItem(`coyg_revision:gauge:${matchId}`); } catch { return null; } };
  const revision = storedRevision();
  const current = () => storedRevision() === revision && !latestGaugeWrite.has(matchId);
  try {
    const res = await fetch(
      `${API_BASE}/api/coyg/gauge?match=${encodeURIComponent(matchId)}`,
      { cache: 'no-store', headers: { 'x-coyg-anon-key': identity.value } },
    );
    if (res.ok) {
      const body = privacySafeGauge((await res.json()) as GaugeSummary);
      if (typeof body.count === 'number') {
        let accepted = false;
        const committed = await mutateGamePassport((state) => { accepted = current(); return state; }, identity.generation,
          () => { if (accepted) writeJSON(`${GAUGE_CACHE}:${matchId}`, body); });
        if (!committed || !(await isLocalCommunityGenerationCurrent(identity.generation))) return { gauge: GAUGE_EMPTY, online: false };
        if (!accepted || !current()) return { gauge: fallbackGauge(matchId), online: false };
        return { gauge: body, online: true };
      }
    }
  } catch {
    /* offline */
  }
  return { gauge: fallbackGauge(matchId), online: false };
}

export async function postGauge(
  matchId: string,
  patch: { rating?: number | null; mood?: Mood | null; moment?: string | null },
): Promise<GaugeSummary | null> {
  if (!hasCommunityConsent()) return null;
  const identity = await captureLocalCommunitySnapshot(getWritableAnonKey);
  const anonKey = identity.value;
  if (!anonKey) { announceWritePersistenceFailure('gauge'); return null; }
  const operationId = `gauge:${matchId}:${crypto.randomUUID()}`;
  let revision = 0;
  latestGaugeWrite.set(matchId, operationId);
  const current = () => {
    try { return latestGaugeWrite.get(matchId) === operationId && revision > 0 && Number(localStorage.getItem(`coyg_revision:gauge:${matchId}`)) === revision; }
    catch { return false; }
  };
  try {
    const delivery = await transmitIfLocalCommunityGeneration(identity.generation, async () => {
      revision = await nextClientRevision('gauge', matchId);
      const payload = { matchId, anonKey, ...patch, revision, operationId, dataEpoch: STANDALONE_DATA_EPOCH, ageConfirmed: true, communityPolicy: COMMUNITY_POLICY_VERSION };
      return fetch(`${API_BASE}/api/coyg/gauge`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload), signal: AbortSignal.timeout(10_000),
      });
    }, 'shared');
    if (!delivery.transmitted) return null;
    const res = delivery.value;
    if (res.ok) {
      const body = privacySafeGauge((await res.json()) as GaugeSummary);
      let accepted = false;
      const committed = await mutateGamePassport((state) => { accepted = current(); return state; }, identity.generation, () => {
        if (!accepted) return;
        writeJSON(`${GAUGE_CACHE}:${matchId}`, body);
        markParticipated(matchId);
      });
      if (!committed || !accepted || !(await isLocalCommunityGenerationCurrent(identity.generation)) || !current()) return null;
      return body;
    }
    if (res.status !== 429 && res.status < 500) announceWritePersistenceFailure('gauge');
  } catch {
    /* Creative reactions are online-only across safety boundaries. */
  } finally {
    if (latestGaugeWrite.get(matchId) === operationId) latestGaugeWrite.delete(matchId);
  }
  return null;
}

// ── Predictions ──────────────────────────────────────────────────────────────

export const PREDICTION_EMPTY: PredictionSummary = {
  counts: { W: 0, D: 0, L: 0 },
  total: 0,
  confidence: null,
  aggregateAvailable: false,
  mine: null,
};

export function privacySafePrediction(summary: PredictionSummary): PredictionSummary {
  return { ...PREDICTION_EMPTY, mine: summary.mine === 'W' || summary.mine === 'D' || summary.mine === 'L' ? summary.mine : null };
}

export function fallbackPrediction(matchId?: string): PredictionSummary {
  const cached = readJSON<PredictionSummary>(matchId ? `${PREDICT_CACHE}:${matchId}` : PREDICT_CACHE);
  return cached ? privacySafePrediction(cached) : PREDICTION_EMPTY;
}

export async function fetchPrediction(matchId: string): Promise<{ prediction: PredictionSummary; online: boolean }> {
  const writeEpoch = predictionWriteEpoch.get(matchId) ?? 0;
  const callVersion = readGamePassport().callVersions[matchId] ?? '';
  const storedRevision = () => { try { return localStorage.getItem(`coyg_revision:prediction:${matchId}`); } catch { return null; } };
  const revision = storedRevision();
  const current = () => (predictionWriteEpoch.get(matchId) ?? 0) === writeEpoch && !latestPredictionWrite.has(matchId)
    && predictionReadEpoch.get(matchId) === readEpoch && storedRevision() === revision
    && (readGamePassport().callVersions[matchId] ?? '') === callVersion;
  const readEpoch = (predictionReadEpoch.get(matchId) ?? 0) + 1;
  predictionReadEpoch.set(matchId, readEpoch);
  const identity = await captureLocalCommunitySnapshot(getAnonKey);
  if (!identity.value) return { prediction: PREDICTION_EMPTY, online: false };
  try {
    const res = await fetch(
      `${API_BASE}/api/coyg/predictions?match=${encodeURIComponent(matchId)}`,
      { cache: 'no-store', headers: { 'x-coyg-anon-key': identity.value } },
    );
    if (res.ok) {
      const body = privacySafePrediction((await res.json()) as PredictionSummary);
      if (body && typeof body.total === 'number') {
        if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return { prediction: PREDICTION_EMPTY, online: false };
        if (!current()) return { prediction: fallbackPrediction(matchId), online: false };
        // Null may mean server retention expired; do not destroy an old receipt.
        const cache = () => { writeJSON(`${PREDICT_CACHE}:${matchId}`, body); if (body.mine) markParticipated(matchId); };
        if (body.mine) await recordConfirmedPrediction(matchId, body.mine, identity.generation, current, undefined, cache);
        else {
          let accepted = false;
          await mutateGamePassport((state) => { accepted = current(); return state; }, identity.generation, () => { if (accepted) cache(); });
        }
        if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return { prediction: PREDICTION_EMPTY, online: false };
        if (!current()) return { prediction: fallbackPrediction(matchId), online: false };
        return { prediction: body, online: true };
      }
    }
  } catch {
    /* offline */
  }
  return { prediction: fallbackPrediction(matchId), online: false };
}

export async function postPrediction(
  matchId: string,
  pick: PredictionPick | null,
): Promise<PredictionSummary | null> {
  if (!hasCommunityConsent()) return null;
  predictionWriteEpoch.set(matchId, (predictionWriteEpoch.get(matchId) ?? 0) + 1);
  const identity = await captureLocalCommunitySnapshot(getWritableAnonKey);
  const anonKey = identity.value;
  if (!anonKey) { announceWritePersistenceFailure('prediction'); return null; }
  const operationId = `prediction:${matchId}:${crypto.randomUUID()}`;
  let revision = 0;
  latestPredictionWrite.set(matchId, operationId);
  const currentWrite = () => {
    try { return latestPredictionWrite.get(matchId) === operationId && revision > 0 && Number(localStorage.getItem(`coyg_revision:prediction:${matchId}`)) === revision; }
    catch { return false; }
  };
  try {
    const delivery = await transmitIfLocalCommunityGeneration(identity.generation, async () => {
      revision = await nextClientRevision('prediction', matchId);
      const payload = { matchId, anonKey, pick, revision, operationId, dataEpoch: STANDALONE_DATA_EPOCH, ageConfirmed: true, communityPolicy: COMMUNITY_POLICY_VERSION };
      return fetch(`${API_BASE}/api/coyg/predictions`, {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload), signal: AbortSignal.timeout(10_000),
      });
    }, 'shared');
    if (!delivery.transmitted) return null;
    const res = delivery.value;
    if (res.ok) {
      const body = privacySafePrediction((await res.json()) as PredictionSummary);
      if (!currentWrite()) return null;
      if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return null;
      const committed = await recordConfirmedPrediction(matchId, body.mine, identity.generation, currentWrite, operationId,
        () => { writeJSON(`${PREDICT_CACHE}:${matchId}`, body); if (body.mine) markParticipated(matchId); });
      if (!committed) return null;
      if (!(await isLocalCommunityGenerationCurrent(identity.generation))) return null;
      if (!currentWrite()) return null;
      return body;
    }
    if (res.status !== 429 && res.status < 500) announceWritePersistenceFailure('prediction');
  } catch {
    /* Creative predictions are online-only across safety boundaries. */
  } finally {
    if (latestPredictionWrite.get(matchId) === operationId) latestPredictionWrite.delete(matchId);
  }
  return null;
}

/** Permanently remove the server-side community data tied to this device key. */
export async function eraseMyData(): Promise<boolean> {
  const anonKey = getWritableAnonKey();
  if (!anonKey) return false;
  return eraseLocalCommunityData(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/coyg/data`, {
        method: 'DELETE',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ anonKey, dataEpoch: STANDALONE_DATA_EPOCH }),
        signal: AbortSignal.timeout(10_000),
      });
      return res.ok;
    } catch {
      return false;
    }
  });
}

/** Download the device-linked community record as portable JSON. */
export async function downloadMyData(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/api/coyg/data`, privateRead());
    if (!res.ok) return false;
    const value = await res.json();
    const blob = new Blob([JSON.stringify(value, null, 2)], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `coyg-data-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(href);
    return true;
  } catch {
    return false;
  }
}
